/**
 * Ubicaciones Queries Service - TanStack Query Integration
 *
 * Este servicio encapsula todas las queries relacionadas con ubicaciones
 * (locations con type != WAREHOUSE) usando TanStack Query.
 *
 * Incluye funcionalidad especial para estructura jerárquica de 7 niveles:
 * WAREHOUSE (raíz) → SALON → ROW → FRONT/BACK → RACK → LEVEL → POSITION
 *
 * Características:
 * - Transformación de lista plana a estructura de árbol
 * - Cache automático con staleTime y gcTime configurables
 * - Invalidación de cache inteligente
 * - Signals reactivos para estado de loading/error/data
 *
 * @see https://tanstack.com/query/latest/docs/framework/angular/overview
 */

import { Injectable, Signal, inject } from '@angular/core';
import {
  injectQuery,
  injectMutation,
  injectQueryClient,
  CreateQueryResult,
  CreateMutationResult,
} from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

// Services
import { LocationQueriesApiService } from '../services/location-queries-api.service';

// Models
import {
  LocationModel,
  LocationListModel,
  LocationCreateModel,
  LocationTypeFilterModel,
  TypeLocationListModel,
  CountryModel,
  CountryListModel,
  CountryFilterModel,
  StateCountryModel,
  StateCountryListModel,
  StateCountryFilterModel,
  CityModel,
  CityListModel,
  CityFilterModel,
} from '@kanzi-apes/kanzi-models';

// Query Keys
import {
  locationsQueryKeys,
  buildUbicacionFilters,
  buildChildLocationFilters,
  UbicacionFilterModel,
} from './locations-query-keys';

/**
 * Interfaz para nodo del árbol jerárquico
 */
export interface LocationTreeNode extends LocationModel {
  children?: LocationTreeNode[];
  level?: number;           // Nivel en el árbol (0 = raíz)
  path?: string[];         // Ruta completa de nombres
  expanded?: boolean;      // Estado de expansión (para UI)
}

@Injectable({
  providedIn: 'root',
})
export class UbicacionesQueriesService {
  private apiService = inject(LocationQueriesApiService);
  private queryClient = injectQueryClient();

  // ==================== QUERIES ====================

  /**
   * Query para listar Ubicaciones (locations con type != WAREHOUSE)
   *
   * @param filters - Signal con filtros de búsqueda
   * @param enabled - Signal opcional que indica si la query está habilitada (default: true)
   * @returns CreateQueryResult con lista de ubicaciones
   *
   * @example
   * ```typescript
   * filters = signal<UbicacionFilterModel | null>({
   *   page: 1,
   *   page_size: 100,
   *   type__name: 'RACK'
   * });
   * isEnabled = signal(true);
   * ubicacionesQuery = this.queries.useUbicacionesList(this.filters, this.isEnabled);
   *
   * // En template:
   * @if (ubicacionesQuery.data(); as ubicaciones) {
   *   <p-dataView [value]="ubicaciones.results"></p-dataView>
   * }
   * ```
   */
  useUbicacionesList(
    filters: Signal<UbicacionFilterModel | null>,
    enabled?: Signal<boolean>
  ): CreateQueryResult<LocationListModel, Error> {
    return injectQuery(() => ({
      queryKey: locationsQueryKeys.ubicacionesList(filters()),
      queryFn: async () => {
        // Construir filtros excluyendo WAREHOUSE
        const ubicacionFilters = buildUbicacionFilters(filters());

        return lastValueFrom(
          this.apiService.getLocationsList(ubicacionFilters)
        );
      },
      enabled: enabled ? enabled() : true,
      staleTime: 1000 * 60 * 5, // 5 minutos
      gcTime: 1000 * 60 * 30,   // 30 minutos
      retry: 1,
    }));
  }

  /**
   * Query para obtener ubicaciones en estructura de árbol
   *
   * Esta query transforma la lista plana de ubicaciones en una estructura
   * jerárquica usando las relaciones parent-child.
   *
   * @param filters - Signal con filtros de búsqueda
   * @param enabled - Signal que determina si la query debe ejecutarse (opcional)
   * @returns CreateQueryResult con estructura de árbol
   *
   * @example
   * ```typescript
   * filters = signal<UbicacionFilterModel | null>(null);
   * hasFilters = computed(() => !!filters()?.search || !!filters()?.type_id);
   * treeQuery = this.queries.useUbicacionesTree(this.filters, hasFilters);
   *
   * // En template con TreeTable:
   * @if (treeQuery.data(); as tree) {
   *   <p-treeTable [value]="tree">
   *     <ng-template pTemplate="body" let-node>
   *       <tr>
   *         <td>{{ node.data.name }}</td>
   *         <td>{{ node.data.type?.name }}</td>
   *       </tr>
   *     </ng-template>
   *   </p-treeTable>
   * }
   * ```
   */
  useUbicacionesTree(
    filters: Signal<UbicacionFilterModel | null>,
    enabled?: Signal<boolean>
  ): CreateQueryResult<LocationTreeNode[], Error> {
    return injectQuery(() => ({
      queryKey: locationsQueryKeys.ubicacionesTree(filters()),
      queryFn: async () => {
        // Obtener todas las ubicaciones sin paginación
        const ubicacionFilters = buildUbicacionFilters({
          ...filters(),
          page_size: 10000, // Traer todas para construir árbol
        });

        const data = await lastValueFrom(
          this.apiService.getLocationsList(ubicacionFilters)
        );

        // Transformar lista plana a estructura de árbol
        return this.transformToTree(data.results);
      },
      enabled: enabled ? enabled() : true,
      staleTime: 1000 * 60 * 5, // 5 minutos
      gcTime: 1000 * 60 * 30,
      retry: 1,
    }));
  }

  /**
   * Query para obtener ubicaciones hijas en estructura de árbol sin exclusión de tipo.
   *
   * A diferencia de useUbicacionesTree, esta query NO excluye ubicaciones tipo WAREHOUSE.
   * Es útil para obtener los hijos directos de una locación (ej: hijos de una Store)
   * donde no se conoce el tipo de los hijos.
   *
   * @param filters - Signal con filtros (típicamente solo parent_id)
   * @param enabled - Signal que determina si la query debe ejecutarse
   * @returns CreateQueryResult con estructura de árbol
   */
  useChildLocationsTree(
    filters: Signal<UbicacionFilterModel | null>,
    enabled?: Signal<boolean>
  ): CreateQueryResult<LocationTreeNode[], Error> {
    return injectQuery(() => ({
      queryKey: locationsQueryKeys.childLocationsTree(filters()),
      queryFn: async () => {
        // Construir filtros SIN exclusión de tipo
        const childFilters = buildChildLocationFilters({
          ...filters(),
          page_size: 10000,
        });

        const data = await lastValueFrom(
          this.apiService.getLocationsList(childFilters)
        );

        return this.transformToTree(data.results);
      },
      enabled: enabled ? enabled() : true,
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      retry: 1,
    }));
  }

  /**
   * Query for a paginated list of direct children by parent_id (drill-down navigation).
   *
   * Unlike useChildLocationsTree (which fetches up to 10000 records to build a full tree),
   * this query returns a flat, backend-paginated list of the direct children of a given
   * parent. It never assumes the `type` of the parent or the children: navigability is
   * resolved purely from the parent_id relationship.
   *
   * Behavior by filter:
   * - When `parent_id` is present, returns the direct children of that node.
   * - When `parent_id` is absent (root level), the caller is expected to provide a root
   *   filter (e.g. `type__name: 'WAREHOUSE,STORE'`) so the backend returns the root nodes
   *   of the active customer.
   *
   * @param filters - Signal with the drill-down filters (parent_id, customer_id, page, page_size, search, type_id)
   * @param enabled - Optional signal indicating whether the query is enabled (default: true)
   * @returns CreateQueryResult with a flat paginated LocationListModel
   *
   * @example
   * ```typescript
   * filters = computed<UbicacionFilterModel>(() => ({
   *   parent_id: this.currentParentId() ?? undefined,
   *   customer_id: this.customerSelected()?.id,
   *   page: 1,
   *   page_size: 25,
   * }));
   * childrenQuery = this.queries.useUbicacionChildren(this.filters, this.isEnabled);
   * ```
   */
  useUbicacionChildren(
    filters: Signal<UbicacionFilterModel | null>,
    enabled?: Signal<boolean>
  ): CreateQueryResult<LocationListModel, Error> {
    return injectQuery(() => ({
      queryKey: locationsQueryKeys.ubicacionesChildren(filters()),
      queryFn: async () => {
        // No type exclusion: children/roots can be any type (drill-down is parent-based).
        const childFilters = buildChildLocationFilters(filters());

        return lastValueFrom(
          this.apiService.getLocationsList(childFilters)
        );
      },
      enabled: enabled ? enabled() : true,
      staleTime: 1000 * 60 * 5, // 5 minutos
      gcTime: 1000 * 60 * 30,   // 30 minutos
      retry: 1,
    }));
  }

  /**
   * Fetches the direct-children count of a location on demand (optimistic navigability).
   *
   * Used by the drill-down navigation to decide, at click time, whether a row is navigable
   * without relying on the node's `type`. Requests a single record (`page_size: 1`) and reads
   * the `count` from the paginated response, so it is cheap. The result is cached by TanStack
   * Query, so navigating right after the check reuses the data.
   *
   * @param parentId - The id of the location whose children are being checked
   * @param customerId - (optional) The active customer id to scope the query
   * @returns A promise resolving to the number of direct children of the location
   */
  async fetchChildrenCount(
    parentId: number,
    customerId?: number | null
  ): Promise<number> {
    const filters: UbicacionFilterModel = {
      parent_id: parentId,
      customer_id: customerId ?? undefined,
      page: 1,
      page_size: 1,
    };

    const data = await this.queryClient.fetchQuery({
      queryKey: locationsQueryKeys.ubicacionesChildren(filters),
      queryFn: () =>
        lastValueFrom(
          this.apiService.getLocationsList(buildChildLocationFilters(filters))
        ),
      staleTime: 1000 * 60 * 5,
    });

    return data?.count ?? 0;
  }

  /**
   * Query para obtener detalle de una Ubicación
   *
   * @param ubicacionId - Signal con el ID de la ubicación
   * @param enabled - Signal que indica si la query está habilitada
   * @returns CreateQueryResult con detalle de la ubicación
   *
   * @example
   * ```typescript
   * selectedUbicacionId = signal<number | null>(null);
   * isDetailEnabled = signal(false);
   *
   * ubicacionQuery = this.queries.useUbicacionDetail(
   *   this.selectedUbicacionId,
   *   this.isDetailEnabled
   * );
   *
   * // Computed signal para null safety
   * ubicacion = computed(() => {
   *   const isLoading = this.ubicacionQuery.isPending() ||
   *                     this.ubicacionQuery.isFetching();
   *   return isLoading ? null : this.ubicacionQuery.data();
   * });
   * ```
   */
  useUbicacionDetail(
    ubicacionId: Signal<number | null>,
    enabled: Signal<boolean>
  ): CreateQueryResult<LocationModel, Error> {
    return injectQuery(() => ({
      queryKey: ubicacionId()
        ? locationsQueryKeys.ubicacionDetail(ubicacionId()!)
        : ['disabled'],
      queryFn: async () => {
        const id = ubicacionId();
        if (!id) {
          throw new Error('Ubicacion ID is required');
        }
        return lastValueFrom(this.apiService.getLocationDetail(id));
      },
      enabled: enabled() && ubicacionId() !== null,
      staleTime: 1000 * 60 * 2, // 2 minutos
      gcTime: 1000 * 60 * 30,
      retry: 1,
    }));
  }

  /**
   * Query para obtener tipos de ubicaciones
   *
   * @param filters - Signal con filtros opcionales
   * @returns CreateQueryResult con lista de tipos
   *
   * @example
   * ```typescript
   * filters = signal<LocationTypeFilterModel | null>(null);
   * typesQuery = this.queries.useLocationTypes(this.filters);
   *
   * // Para dropdown:
   * @if (typesQuery.data(); as types) {
   *   <p-dropdown
   *     [options]="types.results"
   *     optionLabel="name"
   *     optionValue="id"
   *   ></p-dropdown>
   * }
   * ```
   */
  useLocationTypes(
    filters: Signal<LocationTypeFilterModel | null>
  ): CreateQueryResult<TypeLocationListModel, Error> {
    return injectQuery(() => ({
      queryKey: locationsQueryKeys.typesList(filters()),
      queryFn: async () => {
        return lastValueFrom(
          this.apiService.getLocationTypes(filters())
        );
      },
      enabled: true,
      staleTime: 1000 * 60 * 30, // 30 minutos - tipos cambian poco
      gcTime: 1000 * 60 * 60,    // 1 hora
      retry: 1,
    }));
  }

  // ==================== MUTATIONS ====================

  /**
   * Mutation para crear una nueva Ubicación
   *
   * @returns CreateMutationResult con funciones para ejecutar la mutación
   *
   * @example
   * ```typescript
   * createMutation = this.queries.useCreateUbicacion();
   *
   * onCreateUbicacion(ubicacion: LocationCreateModel) {
   *   this.createMutation.mutate(ubicacion, {
   *     onSuccess: (data) => {
   *       this.messageService.add({
   *         severity: 'success',
   *         summary: 'Ubicación creada',
   *         detail: `Ubicación ${data.name} creada exitosamente`
   *       });
   *       this.router.navigate(['ubicaciones', 'detail', data.id]);
   *     }
   *   });
   * }
   * ```
   */
  useCreateUbicacion(): CreateMutationResult<
    LocationModel,
    Error,
    LocationCreateModel,
    unknown
  > {
    return injectMutation(() => ({
      mutationFn: async (ubicacion: LocationCreateModel) => {
        return lastValueFrom(this.apiService.createLocation(ubicacion));
      },
      onSuccess: () => {
        // Invalidar lista de ubicaciones
        this.queryClient.invalidateQueries({
          queryKey: locationsQueryKeys.ubicaciones(),
        });
      },
    }));
  }

  /**
   * Mutation para actualizar una Ubicación existente
   *
   * @returns CreateMutationResult con funciones para ejecutar la mutación
   *
   * @example
   * ```typescript
   * updateMutation = this.queries.useUpdateUbicacion();
   *
   * onUpdateUbicacion(ubicacion: LocationCreateModel) {
   *   this.updateMutation.mutate(ubicacion);
   * }
   * ```
   */
  useUpdateUbicacion(): CreateMutationResult<
    LocationModel,
    Error,
    LocationCreateModel,
    unknown
  > {
    return injectMutation(() => ({
      mutationFn: async (ubicacion: LocationCreateModel) => {
        return lastValueFrom(this.apiService.updateLocation(ubicacion));
      },
      onSuccess: (data) => {
        // Invalidar detalle de la ubicación actualizada
        if (data.id) {
          this.queryClient.invalidateQueries({
            queryKey: locationsQueryKeys.ubicacionDetail(data.id),
          });
        }

        // Invalidar lista para reflejar cambios
        this.queryClient.invalidateQueries({
          queryKey: locationsQueryKeys.ubicaciones(),
        });
      },
    }));
  }

  /**
   * Mutation para eliminar una Ubicación
   *
   * @returns CreateMutationResult con funciones para ejecutar la mutación
   *
   * @example
   * ```typescript
   * deleteMutation = this.queries.useDeleteUbicacion();
   *
   * onDeleteUbicacion(id: number) {
   *   this.confirmService.confirm({
   *     message: '¿Está seguro de eliminar esta ubicación?',
   *     accept: () => {
   *       this.deleteMutation.mutate(id);
   *     }
   *   });
   * }
   * ```
   */
  useDeleteUbicacion(): CreateMutationResult<
    LocationModel,
    Error,
    number,
    unknown
  > {
    return injectMutation(() => ({
      mutationFn: async (id: number) => {
        return lastValueFrom(this.apiService.deleteLocation(id));
      },
      onSuccess: () => {
        // Invalidar lista de ubicaciones
        this.queryClient.invalidateQueries({
          queryKey: locationsQueryKeys.ubicaciones(),
        });
      },
    }));
  }

  /**
   * Query para obtener lista de países
   *
   * @param filters - Signal con filtros opcionales
   * @returns CreateQueryResult con lista de países
   *
   * @example
   * ```typescript
   * filters = signal<CountryFilterModel | null>(null);
   * countriesQuery = this.queries.useCountries(this.filters);
   *
   * // Para dropdown:
   * @if (countriesQuery.data(); as countries) {
   *   <p-dropdown
   *     [options]="countries.results"
   *     optionLabel="name"
   *     optionValue="id"
   *   ></p-dropdown>
   * }
   * ```
   */
  useCountries(
    filters: Signal<CountryFilterModel | null>
  ): CreateQueryResult<CountryListModel, Error> {
    return injectQuery(() => ({
      queryKey: locationsQueryKeys.countriesList(filters()),
      queryFn: async () => {
        return lastValueFrom(
          this.apiService.getCountries(filters())
        );
      },
      enabled: true,
      staleTime: 1000 * 60 * 60, // 1 hora - países cambian muy poco
      gcTime: 1000 * 60 * 120,   // 2 horas
      retry: 1,
    }));
  }

  /**
   * Query para obtener lista de estados/provincias
   *
   * @param filters - Signal con filtros (típicamente country_id)
   * @param enabled - Signal que determina si la query debe ejecutarse
   * @returns CreateQueryResult con lista de estados
   *
   * @example
   * ```typescript
   * selectedCountryId = signal<number | null>(null);
   * stateFilters = computed(() => ({
   *   country_id: selectedCountryId()
   * }));
   * hasCountry = computed(() => selectedCountryId() !== null);
   *
   * statesQuery = this.queries.useStates(this.stateFilters, this.hasCountry);
   * ```
   */
  useStates(
    filters: Signal<StateCountryFilterModel | null>,
    enabled?: Signal<boolean>
  ): CreateQueryResult<StateCountryListModel, Error> {
    return injectQuery(() => ({
      queryKey: locationsQueryKeys.statesList(filters()),
      queryFn: async () => {
        return lastValueFrom(
          this.apiService.getStates(filters())
        );
      },
      enabled: enabled ? enabled() : true,
      staleTime: 1000 * 60 * 30, // 30 minutos
      gcTime: 1000 * 60 * 60,    // 1 hora
      retry: 1,
    }));
  }

  /**
   * Query para obtener lista de ciudades
   *
   * @param filters - Signal con filtros (típicamente state_id)
   * @param enabled - Signal que determina si la query debe ejecutarse
   * @returns CreateQueryResult con lista de ciudades
   *
   * @example
   * ```typescript
   * selectedStateId = signal<number | null>(null);
   * cityFilters = computed(() => ({
   *   state_id: selectedStateId()
   * }));
   * hasState = computed(() => selectedStateId() !== null);
   *
   * citiesQuery = this.queries.useCities(this.cityFilters, this.hasState);
   * ```
   */
  useCities(
    filters: Signal<CityFilterModel | null>,
    enabled?: Signal<boolean>
  ): CreateQueryResult<CityListModel, Error> {
    return injectQuery(() => ({
      queryKey: locationsQueryKeys.citiesList(filters()),
      queryFn: async () => {
        return lastValueFrom(
          this.apiService.getCities(filters())
        );
      },
      enabled: enabled ? enabled() : true,
      staleTime: 1000 * 60 * 30, // 30 minutos
      gcTime: 1000 * 60 * 60,    // 1 hora
      retry: 1,
    }));
  }

  // ==================== TREE TRANSFORMATION ====================

  /**
   * Transforma una lista plana de ubicaciones en estructura de árbol
   *
   * Algoritmo:
   * 1. Crear mapa de ubicaciones por ID
   * 2. Identificar nodos raíz (sin parent o parent es WAREHOUSE)
   * 3. Para cada ubicación, agregar a children de su parent
   * 4. Calcular nivel y ruta completa
   *
   * @param locations - Lista plana de ubicaciones
   * @returns Array de nodos raíz con sus children anidados
   *
   * @example
   * ```typescript
   * const flatList = [
   *   { id: 1, name: 'Salon A', parent: null, type: { name: 'SALON' } },
   *   { id: 2, name: 'Row 1', parent: 1, type: { name: 'ROW' } },
   *   { id: 3, name: 'Rack A', parent: 2, type: { name: 'RACK' } }
   * ];
   *
   * const tree = this.transformToTree(flatList);
   * // Resultado:
   * // [
   * //   {
   * //     ...salon A,
   * //     children: [
   * //       {
   * //         ...row 1,
   * //         children: [
   * //           { ...rack A, children: [] }
   * //         ]
   * //       }
   * //     ]
   * //   }
   * // ]
   * ```
   */
  transformToTree(locations: LocationModel[]): LocationTreeNode[] {
    // Paso 1: Crear mapa de ubicaciones por ID
    const map = new Map<number, LocationTreeNode>();
    const roots: LocationTreeNode[] = [];

    // Inicializar todos los nodos en el mapa
    locations.forEach((location) => {
      if (!location.id) return; // Skip if no ID
      map.set(location.id, {
        ...location,
        children: [],
        level: 0,
        path: [location.name],
        expanded: false,
      });
    });

    // Paso 2: Construir relaciones parent-child
    locations.forEach((location) => {
      if (!location.id) return; // Skip if no ID
      const node = map.get(location.id);
      if (!node) return;

      // Si tiene parent y el parent existe en el mapa
      if (location.parent && map.has(location.parent)) {
        const parent = map.get(location.parent)!;

        // Agregar como child del parent
        parent.children!.push(node);

        // Calcular nivel y ruta
        node.level = (parent.level || 0) + 1;
        node.path = [...parent.path!, node.name];
      } else {
        // Es un nodo raíz (sin parent o parent no existe)
        roots.push(node);
      }
    });

    // Paso 3: Ordenar children por nombre en cada nivel
    const sortChildren = (nodes: LocationTreeNode[]) => {
      nodes.sort((a, b) => a.name.localeCompare(b.name));
      nodes.forEach((node) => {
        if (node.children && node.children.length > 0) {
          sortChildren(node.children);
        }
      });
    };

    sortChildren(roots);

    return roots;
  }

  /**
   * Encuentra una ubicación en el árbol por ID
   *
   * @param tree - Árbol de ubicaciones
   * @param id - ID de la ubicación a buscar
   * @returns Nodo encontrado o null
   */
  findNodeById(
    tree: LocationTreeNode[],
    id: number
  ): LocationTreeNode | null {
    for (const node of tree) {
      if (node.id === id) {
        return node;
      }

      if (node.children && node.children.length > 0) {
        const found = this.findNodeById(node.children, id);
        if (found) {
          return found;
        }
      }
    }

    return null;
  }

  /**
   * Obtiene todos los nodos hijos (recursivamente) de un nodo
   *
   * @param node - Nodo raíz
   * @returns Array plano con todos los descendientes
   */
  getAllDescendants(node: LocationTreeNode): LocationTreeNode[] {
    const descendants: LocationTreeNode[] = [];

    const traverse = (current: LocationTreeNode) => {
      if (current.children) {
        current.children.forEach((child) => {
          descendants.push(child);
          traverse(child);
        });
      }
    };

    traverse(node);
    return descendants;
  }

  /**
   * Calcula la profundidad máxima del árbol
   *
   * @param tree - Árbol de ubicaciones
   * @returns Profundidad máxima (número de niveles)
   */
  getTreeDepth(tree: LocationTreeNode[]): number {
    let maxDepth = 0;

    const traverse = (nodes: LocationTreeNode[], depth: number) => {
      if (depth > maxDepth) {
        maxDepth = depth;
      }

      nodes.forEach((node) => {
        if (node.children && node.children.length > 0) {
          traverse(node.children, depth + 1);
        }
      });
    };

    traverse(tree, 1);
    return maxDepth;
  }

  // ==================== CACHE MANAGEMENT ====================

  /**
   * Invalida todo el cache relacionado con ubicaciones
   *
   * @example
   * ```typescript
   * // Después de importación masiva:
   * this.queries.invalidateAllUbicaciones();
   * ```
   */
  invalidateAllUbicaciones(): void {
    this.queryClient.invalidateQueries({
      queryKey: locationsQueryKeys.ubicaciones(),
    });
  }

  /**
   * Invalida el cache de una ubicación específica
   *
   * @param ubicacionId - ID de la ubicación
   */
  invalidateUbicacion(ubicacionId: number): void {
    this.queryClient.invalidateQueries({
      queryKey: locationsQueryKeys.ubicacionDetail(ubicacionId),
    });
  }

  /**
   * Pre-carga los datos de una ubicación en el cache
   *
   * @param ubicacionId - ID de la ubicación
   */
  async prefetchUbicacion(ubicacionId: number): Promise<void> {
    await this.queryClient.prefetchQuery({
      queryKey: locationsQueryKeys.ubicacionDetail(ubicacionId),
      queryFn: () =>
        lastValueFrom(this.apiService.getLocationDetail(ubicacionId)),
      staleTime: 1000 * 60 * 2,
    });
  }

  /**
   * Invalida el árbol completo de ubicaciones
   * Útil cuando se crean/eliminan ubicaciones que afectan la jerarquía
   * Invalida todas las variantes del árbol independientemente de los filtros
   */
  invalidateTree(): void {
    // Invalida todas las queries que empiecen con ['locations', 'ubicaciones', 'tree']
    this.queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey as readonly unknown[];
        return (
          key[0] === 'locations' &&
          key[1] === 'ubicaciones' &&
          key[2] === 'tree'
        );
      },
    });
  }
}
